import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { StageBadge, StatusBadge } from "@/lib/projectMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw,
  Upload, Mic, Film, CheckCircle2, XCircle, AlertTriangle, Download, DollarSign, ShieldCheck,
  Youtube, Rocket, Clock, Ban, ExternalLink,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "brief");

  const loadProject = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${id}`); setProject(data); }
    catch (e) { setError(apiError(e)); }
  }, [id]);
  useEffect(() => { loadProject(); }, [loadProject]);

  if (error) return <ErrorState message={error} onRetry={loadProject} />;
  if (!project) return <LoadingState label="Loading project…" />;

  return (
    <div className="animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/pipeline")} className="text-slate-400 hover:text-slate-200" data-testid="back-to-pipeline"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-2xl font-display font-extrabold text-slate-50">{project.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <StageBadge stage={project.stage} /><StatusBadge status={project.stage_status} />
              <span className="text-xs text-slate-500">{project.channel_name} · {project.content_language} · profile v{project.profile_version} (pinned)</span>
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div>Budget ${project.budget_cap} · spent ${(project.cost_actual || 0).toFixed(4)}</div>
        </div>
      </div>

      {project.approval_invalidated && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2.5" data-testid="approval-invalidated-banner">
          <AlertTriangle className="w-4 h-4" /> {project.approval_invalidated_reason}
        </div>
      )}
      {project.storyboard_stale && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2.5" data-testid="storyboard-stale-banner">
          <AlertTriangle className="w-4 h-4" /> Storyboard is stale — the script changed since it was generated. Regenerate the storyboard.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800 mb-6 flex-wrap h-auto">
          {["brief", "script", "storyboard", "assets", "render", "review", "publish"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="data-[state=active]:bg-violet-600 data-[state=active]:text-white capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="brief"><BriefTab project={project} onSaved={loadProject} /></TabsContent>
        <TabsContent value="script"><ScriptTab project={project} onChanged={loadProject} /></TabsContent>
        <TabsContent value="storyboard"><StoryboardTab project={project} onChanged={loadProject} /></TabsContent>
        <TabsContent value="assets"><AssetsTab project={project} /></TabsContent>
        <TabsContent value="render"><RenderTab project={project} onChanged={loadProject} /></TabsContent>
        <TabsContent value="review"><ReviewTab project={project} onChanged={loadProject} /></TabsContent>
        <TabsContent value="publish"><PublishTab project={project} onChanged={loadProject} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-[#111827] border border-slate-800/80 rounded-xl p-5 ${className}`}>{children}</div>;
}

/* ------------------------------ Brief ------------------------------ */
function BriefTab({ project, onSaved }) {
  const [f, setF] = useState({
    title: project.title, brief: project.brief || "", objective: project.objective || "",
    target_duration: project.target_duration, budget_cap: project.budget_cap,
    content_language: project.content_language, research_notes: project.research_notes || "",
    research_sources: (project.research_sources || []).join(", "),
    metadata_title: project.metadata?.title || project.title, metadata_description: project.metadata?.description || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}`, { ...f, research_sources: f.research_sources.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Title</Label><Input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="brief-title" /></div>
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Content language</Label><Input className={inputCls} value={f.content_language} onChange={(e) => setF({ ...f, content_language: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Brief</Label><Textarea className={inputCls} value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })} data-testid="brief-text" /></div>
        <div className="sm:col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Objective</Label><Input className={inputCls} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} /></div>
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Target duration (s)</Label><Input type="number" className={inputCls} value={f.target_duration} onChange={(e) => setF({ ...f, target_duration: Number(e.target.value) })} /></div>
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Budget cap ($)</Label><Input type="number" step="0.01" className={inputCls} value={f.budget_cap} onChange={(e) => setF({ ...f, budget_cap: Number(e.target.value) })} data-testid="brief-budget" /></div>
        <div className="sm:col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Research notes</Label><Textarea className={inputCls} value={f.research_notes} onChange={(e) => setF({ ...f, research_notes: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Research sources (comma separated)</Label><Input className={inputCls} value={f.research_sources} onChange={(e) => setF({ ...f, research_sources: e.target.value })} /></div>
      </div>
      <div className="mt-5"><Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="brief-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button></div>
    </Card>
  );
}

/* ------------------------------ Script ------------------------------ */
function ScriptTab({ project, onChanged }) {
  const [versions, setVersions] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const [draft, setDraft] = useState({ hook: "", body: "", ending: "", narration_notes: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${project.id}/scripts`);
    setVersions(data); setSel(data[0] || null);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    try { await api.post(`/projects/${project.id}/scripts/generate`); toast.success("AI script generated"); await load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setGen(false); }
  };
  const saveManual = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/scripts`, {
        hook: draft.hook, body: draft.body.split("\n").filter(Boolean), ending: draft.ending, narration_notes: draft.narration_notes,
      });
      toast.success("Script version saved"); setDraft({ hook: "", body: "", ending: "", narration_notes: "" }); await load(); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!versions) return <LoadingState />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-slate-100">Script</h3>
            <Button onClick={generate} disabled={gen} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="script-generate">
              {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate with AI</>}
            </Button>
          </div>
          {sel ? (
            <div data-testid="script-view">
              <div className="text-xs text-slate-500 mb-3">Version {sel.version} · {sel.source}{sel.model ? ` · ${sel.model}` : ""}</div>
              <p className="text-sm text-violet-300 font-medium mb-1">Hook</p><p className="text-sm text-slate-300 mb-3">{sel.hook}</p>
              <p className="text-sm text-violet-300 font-medium mb-1">Body</p>
              <ul className="text-sm text-slate-300 mb-3 space-y-1 list-disc pl-5">{(sel.body || []).map((b, i) => <li key={`body-${i}`}>{b}</li>)}</ul>
              <p className="text-sm text-violet-300 font-medium mb-1">Ending</p><p className="text-sm text-slate-300">{sel.ending}</p>
            </div>
          ) : <EmptyState title="No script yet" description="Generate with AI or write one manually." />}
        </Card>
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Write / edit a new version</h3>
          <div className="space-y-3">
            <Input className={inputCls} placeholder="Opening hook" value={draft.hook} onChange={(e) => setDraft({ ...draft, hook: e.target.value })} data-testid="manual-hook" />
            <Textarea className={inputCls} placeholder="Body — one section per line" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="manual-body" />
            <Input className={inputCls} placeholder="Ending / CTA" value={draft.ending} onChange={(e) => setDraft({ ...draft, ending: e.target.value })} />
            <Button onClick={saveManual} disabled={busy} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="script-save-manual">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save as new version"}
            </Button>
          </div>
        </Card>
      </div>
      <Card>
        <h3 className="font-display font-semibold text-slate-100 mb-3">Versions</h3>
        <div className="space-y-2" data-testid="script-versions">
          {versions.map((v) => (
            <button key={v.id} onClick={() => setSel(v)} className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${sel?.id === v.id ? "border-violet-500/50 bg-violet-500/10 text-violet-200" : "border-slate-800 text-slate-300 hover:bg-slate-800/50"}`}>
              <div className="flex justify-between"><span>v{v.version} · {v.source}</span><span className="text-xs text-slate-500">{new Date(v.created_at).toLocaleTimeString()}</span></div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------------------------- Storyboard ---------------------------- */
function StoryboardTab({ project, onChanged }) {
  const [scenes, setScenes] = useState(null);
  const [assets, setAssets] = useState([]);
  const [tts, setTts] = useState({});
  const [gen, setGen] = useState(false);

  const load = useCallback(async () => {
    const [s, a, t] = await Promise.all([
      api.get(`/projects/${project.id}/scenes`),
      api.get(`/channels/${project.channel_id}/assets`),
      api.get(`/tts/status`),
    ]);
    setScenes(s.data); setAssets(a.data); setTts(t.data);
  }, [project.id, project.channel_id]);
  useEffect(() => { load(); }, [load]);

  const genStoryboard = async () => {
    setGen(true);
    try { await api.post(`/projects/${project.id}/storyboard/generate`); toast.success("Storyboard generated"); await load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setGen(false); }
  };
  const genCaptions = async () => {
    try { await api.post(`/projects/${project.id}/captions/generate`); toast.success("Captions generated"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addScene = async () => {
    try { await api.post(`/projects/${project.id}/scenes`, { narration_text: "", visual_brief: "", on_screen_text: "", duration: 5, transition: "crossfade" }); await load(); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!scenes) return <LoadingState />;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Button onClick={genStoryboard} disabled={gen} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="storyboard-generate">
          {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate from script</>}
        </Button>
        <Button onClick={addScene} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="add-scene"><Plus className="w-4 h-4 mr-1.5" /> Add scene</Button>
        <Button onClick={genCaptions} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="generate-captions"><RefreshCw className="w-4 h-4 mr-1.5" /> Generate captions</Button>
        <a href={`${BACKEND}/api/projects/${project.id}/captions.srt`} className="text-xs text-violet-300 underline ml-2" data-testid="download-srt">Download SRT</a>
        <a href={`${BACKEND}/api/projects/${project.id}/captions.vtt`} className="text-xs text-violet-300 underline" data-testid="download-vtt">Download VTT</a>
      </div>
      {scenes.length === 0 ? <EmptyState icon={Film} title="No scenes yet" description="Generate a storyboard from your script or add scenes manually." /> : (
        <div className="space-y-4">
          {scenes.map((s, idx) => (
            <SceneCard key={s.id} scene={s} idx={idx} total={scenes.length} projectId={project.id} assets={assets} tts={tts} onChanged={async () => { await load(); onChanged(); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function SceneCard({ scene, idx, total, projectId, assets, tts, onChanged }) {
  const [d, setD] = useState({ ...scene });
  const [busy, setBusy] = useState(false);
  const imgAssets = assets.filter((a) => a.kind === "image");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/scenes/${scene.id}`, {
        narration_text: d.narration_text || "", visual_brief: d.visual_brief || "", on_screen_text: d.on_screen_text || "",
        duration: Number(d.duration) || 5, transition: d.transition || "crossfade", audio_notes: d.audio_notes || "", asset_id: d.asset_id || null,
      });
      toast.success(`Scene ${scene.scene_number} saved`); await onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async () => { await api.delete(`/projects/scenes/${scene.id}`); await onChanged(); };
  const move = async (dir) => {
    const list = assets; // noop; reorder handled below
  };
  const reorder = async (dir) => {
    try {
      const { data: all } = await api.get(`/projects/${projectId}/scenes`);
      const ids = all.map((x) => x.id);
      const i = ids.indexOf(scene.id); const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      await api.post(`/projects/${projectId}/scenes/reorder`, { order: ids });
      await onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const regen = async () => { try { await api.post(`/projects/scenes/${scene.id}/regenerate`); toast.success("Scene regenerated"); await onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const uploadNarr = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/scenes/${scene.id}/narration/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Narration uploaded"); await onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const ttsNarr = async (provider) => {
    if (!tts[provider]) { toast.error(`Connect a ${provider} key in Integrations first`); return; }
    try { await api.post(`/scenes/${scene.id}/narration/tts`, { provider }); toast.success("Narration generated"); await onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="border-l-2 border-l-violet-600/40" >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono px-2 py-0.5 rounded bg-slate-800 text-violet-300 border border-slate-700">Scene {scene.scene_number}</span>
          {scene.voiceover_stale && <span className="text-xs text-amber-400">voiceover stale</span>}
          {scene.captions_stale && <span className="text-xs text-amber-400">captions stale</span>}
          {scene.narration_asset_id && <span className="text-xs text-emerald-400">narration ✓</span>}
          {scene.asset_id && <span className="text-xs text-emerald-400">asset ✓</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => reorder("up")} disabled={idx === 0} className="text-slate-500 hover:text-slate-200 disabled:opacity-30 p-1"><ArrowUp className="w-4 h-4" /></button>
          <button onClick={() => reorder("down")} disabled={idx === total - 1} className="text-slate-500 hover:text-slate-200 disabled:opacity-30 p-1"><ArrowDown className="w-4 h-4" /></button>
          <button onClick={regen} className="text-slate-500 hover:text-violet-300 p-1" data-testid={`regen-scene-${scene.scene_number}`}><RefreshCw className="w-4 h-4" /></button>
          <button onClick={del} className="text-slate-500 hover:text-rose-400 p-1" data-testid={`delete-scene-${scene.scene_number}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label className="text-slate-400 text-xs mb-1 block">Narration text</Label><Textarea className={`${inputCls} min-h-[70px]`} value={d.narration_text || ""} onChange={(e) => setD({ ...d, narration_text: e.target.value })} data-testid={`scene-narration-${scene.scene_number}`} /></div>
        <div><Label className="text-slate-400 text-xs mb-1 block">Visual brief</Label><Input className={inputCls} value={d.visual_brief || ""} onChange={(e) => setD({ ...d, visual_brief: e.target.value })} /></div>
        <div><Label className="text-slate-400 text-xs mb-1 block">On-screen text</Label><Input className={inputCls} value={d.on_screen_text || ""} onChange={(e) => setD({ ...d, on_screen_text: e.target.value })} /></div>
        <div><Label className="text-slate-400 text-xs mb-1 block">Duration (s)</Label><Input type="number" className={inputCls} value={d.duration} onChange={(e) => setD({ ...d, duration: e.target.value })} /></div>
        <div><Label className="text-slate-400 text-xs mb-1 block">Transition</Label>
          <Select value={d.transition} onValueChange={(v) => setD({ ...d, transition: v })}>
            <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200"><SelectItem value="crossfade">Crossfade</SelectItem><SelectItem value="cut">Cut</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2"><Label className="text-slate-400 text-xs mb-1 block">Visual asset</Label>
          <Select value={d.asset_id || "none"} onValueChange={(v) => setD({ ...d, asset_id: v === "none" ? null : v })}>
            <SelectTrigger className={inputCls} data-testid={`scene-asset-${scene.scene_number}`}><SelectValue placeholder="No asset (placeholder)" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="none">No asset (placeholder)</SelectItem>
              {imgAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.filename}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Button onClick={save} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`scene-save-${scene.scene_number}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save scene"}</Button>
        <label className="text-xs text-slate-300 border border-slate-700 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-800 flex items-center gap-1.5">
          <Upload className="w-3.5 h-3.5" /> Upload narration
          <input type="file" accept="audio/*" className="hidden" onChange={(e) => uploadNarr(e.target.files[0])} data-testid={`upload-narration-${scene.scene_number}`} />
        </label>
        <button onClick={() => ttsNarr("elevenlabs")} className={`text-xs border rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 ${tts.elevenlabs ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-800 text-slate-600"}`} data-testid={`tts-eleven-${scene.scene_number}`}><Mic className="w-3.5 h-3.5" /> ElevenLabs TTS{!tts.elevenlabs && " (not connected)"}</button>
        <button onClick={() => ttsNarr("openai_tts")} className={`text-xs border rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 ${tts.openai_tts ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-800 text-slate-600"}`} data-testid={`tts-openai-${scene.scene_number}`}><Mic className="w-3.5 h-3.5" /> OpenAI TTS{!tts.openai_tts && " (not connected)"}</button>
      </div>
      {(scene.captions || []).length > 0 && (
        <div className="mt-3 text-xs text-slate-500" data-testid={`scene-captions-${scene.scene_number}`}>Captions: {scene.captions.map((c) => c.text).join(" · ")}</div>
      )}
    </Card>
  );
}

/* ------------------------------ Assets ------------------------------ */
function AssetsTab({ project }) {
  const [assets, setAssets] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/channels/${project.channel_id}/assets`); setAssets(data); }, [project.channel_id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("source", "uploaded");
    try { await api.post(`/channels/${project.channel_id}/assets`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Asset uploaded"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  const del = async (aid) => { await api.delete(`/assets/${aid}`); await load(); };
  if (!assets) return <LoadingState />;
  return (
    <Card>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-semibold text-slate-100">Channel asset library</h3>
        <label className="bg-violet-600 hover:bg-violet-500 text-white text-sm px-3 py-2 rounded-lg cursor-pointer flex items-center gap-1.5" data-testid="asset-upload-label">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          <input type="file" accept="image/*,audio/*,video/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="asset-upload-input" />
        </label>
      </div>
      {assets.length === 0 ? <EmptyState title="No assets yet" description="Upload images, audio or clips to reuse across scenes." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {assets.map((a) => (
            <div key={a.id} className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden" data-testid={`asset-${a.id}`}>
              {a.kind === "image" ? <img src={`${BACKEND}${a.url}`} alt={a.filename} className="w-full h-28 object-cover" /> :
                <div className="w-full h-28 flex items-center justify-center text-slate-500 text-xs uppercase">{a.kind}</div>}
              <div className="p-2">
                <div className="text-xs text-slate-300 truncate">{a.filename}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{a.source}</span>
                  <button onClick={() => del(a.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------ Render ------------------------------ */
function RenderTab({ project, onChanged }) {
  const [renders, setRenders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get(`/projects/${project.id}/renders`); setRenders(data); }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const startRender = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/render`);
      toast.success("Render queued");
      setPolling(true);
      const iv = setInterval(async () => {
        const { data } = await api.get(`/projects/${project.id}/renders`);
        setRenders(data);
        if (data[0] && ["succeeded", "failed", "cancelled"].includes(data[0].status)) { clearInterval(iv); setPolling(false); onChanged(); }
      }, 3000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async (job_id) => { try { await api.post(`/projects/${project.id}/render/${job_id}/cancel`); toast.success("Cancel requested"); await load(); } catch (e) { toast.error(apiError(e)); } };

  if (!renders) return <LoadingState />;
  const active = renders[0] && ["waiting", "running"].includes(renders[0].status);
  return (
    <div className="space-y-5">
      <Card>
        <div className="flex items-center justify-between">
          <div><h3 className="font-display font-semibold text-slate-100">Render</h3><p className="text-sm text-slate-500 mt-1">Builds an MP4 from scenes (missing assets use labeled placeholders; fixture music bed with ducking).</p></div>
          <Button onClick={startRender} disabled={busy || active} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="render-button">
            {busy || active || polling ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Rendering…</> : <><Film className="w-4 h-4 mr-1.5" /> Render video</>}
          </Button>
        </div>
      </Card>
      {renders.length === 0 ? <EmptyState icon={Film} title="No renders yet" description="Render your project to produce a playable MP4." /> : (
        <div className="space-y-4">
          {renders.map((r) => (
            <Card key={r.id} data-testid={`render-version-${r.version}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-mono px-2 py-0.5 rounded bg-slate-800 text-violet-300 border border-slate-700">Render v{r.version}</span>
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status} />
                  {["waiting", "running"].includes(r.status) && <button onClick={() => cancel(r.job_id)} className="text-xs text-rose-400" data-testid={`cancel-render-${r.version}`}>Cancel</button>}
                </div>
              </div>
              {r.status === "succeeded" && (
                <div className="flex flex-col items-center">
                  <video controls playsInline className="rounded-lg bg-black max-w-[280px]" style={{ aspectRatio: project.aspect_ratio === "9:16" ? "9/16" : "16/9" }}
                    src={`${BACKEND}/api/projects/${project.id}/renders/${r.id}/video`} data-testid={`render-video-${r.version}`} />
                  <a href={`${BACKEND}/api/projects/${project.id}/renders/${r.id}/video`} download className="text-xs text-violet-300 underline mt-2" data-testid={`render-download-${r.version}`}>Download MP4</a>
                </div>
              )}
              {r.status === "failed" && <p className="text-sm text-rose-400">Render failed — you can retry.</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Review ------------------------------ */
function ReviewTab({ project, onChanged }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [meta, setMeta] = useState({ title: "", description: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${project.id}/review`);
    setData(data); setMeta({ title: data.metadata?.title || "", description: data.metadata?.description || "" });
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => { try { await api.post(`/projects/${project.id}/approve`); toast.success("Approved"); await load(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const requestChanges = async () => {
    if (!comment.trim()) { toast.error("A comment is required"); return; }
    try { await api.post(`/projects/${project.id}/request-changes`, { comment }); toast.success("Changes requested"); setComment(""); await load(); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const saveMeta = async () => { try { await api.put(`/projects/${project.id}/metadata`, meta); toast.success("Metadata saved"); await load(); } catch (e) { toast.error(apiError(e)); } };
  const ready = async () => { try { await api.post(`/projects/${project.id}/ready-for-scheduling`); toast.success("Marked ready for scheduling"); await load(); onChanged(); } catch (e) { toast.error(apiError(e)); } };

  if (!data) return <LoadingState />;
  const q = data.quality;
  const icon = (s) => s === "pass" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : s === "warn" ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <XCircle className="w-4 h-4 text-rose-400" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        {data.latest_render ? (
          <Card>
            <h3 className="font-display font-semibold text-slate-100 mb-3">Latest render · v{data.latest_render.version}</h3>
            <div className="flex justify-center">
              <video controls playsInline className="rounded-lg bg-black max-w-[280px]" style={{ aspectRatio: project.aspect_ratio === "9:16" ? "9/16" : "16/9" }}
                src={`${BACKEND}/api/projects/${project.id}/renders/${data.latest_render.id}/video`} data-testid="review-video" />
            </div>
          </Card>
        ) : <Card><EmptyState icon={Film} title="No successful render" description="Render the project before reviewing." /></Card>}

        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Quality checks <span className="text-xs text-slate-500 font-normal">(automated)</span></h3>
          <div className="space-y-2" data-testid="quality-checks">
            {q.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2.5 text-sm">
                {icon(c.status)}
                <div><span className="text-slate-200">{c.label}</span><p className="text-xs text-slate-500">{c.detail}</p></div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Metadata</h3>
          <div className="space-y-3">
            <Input className={inputCls} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} placeholder="Video title" data-testid="meta-title" />
            <Textarea className={inputCls} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} placeholder="Description" data-testid="meta-description" />
            <Button onClick={saveMeta} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700">Save metadata</Button>
          </div>
        </Card>
      </div>

      <div className="space-y-5">
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Approval</h3>
          {data.approval ? (
            <div className="text-sm text-emerald-400 flex items-center gap-2 mb-3" data-testid="approval-status"><ShieldCheck className="w-4 h-4" /> Approved · render v{data.approval.render_version}</div>
          ) : (
            <div className="text-sm text-slate-400 mb-3" data-testid="approval-status">Not approved{data.approval_invalidated ? " (invalidated)" : ""}.</div>
          )}
          <Button onClick={approve} disabled={!data.latest_render} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white mb-2" data-testid="approve-button">Approve</Button>
          {data.approval && <Button onClick={ready} className="w-full bg-violet-600 hover:bg-violet-500 text-white mb-2" data-testid="ready-scheduling-button">Ready for scheduling</Button>}
          <Textarea className={`${inputCls} mb-2`} placeholder="Comment (required to request changes)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="request-changes-comment" />
          <Button onClick={requestChanges} variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="request-changes-button">Request changes</Button>
        </Card>
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3 flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> Cost summary</h3>
          <div className="text-sm text-slate-300">Actual: ${(data.costs.total_actual || 0).toFixed(4)}{data.costs.has_unknown && <span className="text-amber-400 text-xs ml-1">+unknown</span>}</div>
          <div className="text-xs text-slate-500 mt-1">{data.costs.entries.length} cost entries</div>
        </Card>
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3">Comments</h3>
          {data.comments.length === 0 ? <p className="text-sm text-slate-500">No comments yet.</p> : (
            <div className="space-y-2" data-testid="comments-list">
              {data.comments.map((c) => (
                <div key={c.id} className="text-sm border-l-2 border-slate-700 pl-3">
                  <div className="text-slate-300">{c.text}</div>
                  <div className="text-xs text-slate-600">{c.kind} · {new Date(c.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


/* ------------------------------ Publish ------------------------------ */
const PUB_STATE = {
  draft: { label: "Draft / queued", cls: "bg-slate-700/40 text-slate-300" },
  uploading: { label: "Uploading", cls: "bg-blue-500/15 text-blue-300" },
  uploaded: { label: "Uploaded (private)", cls: "bg-indigo-500/15 text-indigo-300" },
  processing: { label: "Processing on YouTube", cls: "bg-amber-500/15 text-amber-300" },
  scheduled: { label: "Scheduled", cls: "bg-violet-500/15 text-violet-300" },
  published: { label: "Published", cls: "bg-emerald-500/15 text-emerald-300" },
  failed: { label: "Failed", cls: "bg-rose-500/15 text-rose-300" },
  cancelled: { label: "Cancelled", cls: "bg-slate-700/40 text-slate-400" },
};

function PublishTab({ project, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ privacy_status: "private", schedule: false, publish_at: "", made_for_kids: false, synthetic_media: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${project.id}/publish`);
    setData(data);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const intent = data?.intent;
  const active = intent && ["draft", "uploading", "processing"].includes(intent.status);
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [active, load]);

  const conn = data?.connection;
  const approved = project.approval && ["approved", "scheduled"].includes(project.stage);

  const publish = async () => {
    setBusy(true);
    try {
      const body = {
        privacy_status: form.privacy_status,
        publish_at: form.schedule && form.publish_at ? new Date(form.publish_at).toISOString() : null,
        made_for_kids: form.made_for_kids, synthetic_media: form.synthetic_media,
      };
      await api.post(`/projects/${project.id}/publish`, body);
      toast.success("Publication queued");
      await load(); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.post(`/publish-intents/${intent.id}/cancel`); toast.success("Cancel requested"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <LoadingState />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        {/* Connection state (honest) */}
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-800/70 border border-slate-700/60 flex items-center justify-center"><Youtube className="w-5 h-5 text-rose-400" /></div>
              <div>
                <h3 className="font-display font-semibold text-slate-100">YouTube connection</h3>
                {conn?.connected ? (
                  <p className="text-xs text-emerald-400" data-testid="publish-connection-state">Connected · {conn.youtube_channel_title || conn.youtube_channel_id}</p>
                ) : (
                  <p className="text-xs text-amber-300" data-testid="publish-connection-state">Not connected — publishing is unavailable until you connect YouTube.</p>
                )}
              </div>
            </div>
            {!conn?.connected && (
              <Button onClick={() => navigate("/integrations")} size="sm" className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="goto-integrations">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Integrations
              </Button>
            )}
          </div>
        </Card>

        {/* Publish form / status */}
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-4">Publish to YouTube</h3>
          {!approved ? (
            <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3" data-testid="publish-not-approved">
              <AlertTriangle className="w-4 h-4" /> Only an approved project can be published. Approve a render in the Review tab first.
            </div>
          ) : intent && ["draft", "uploading", "uploaded", "processing", "scheduled", "published"].includes(intent.status) ? (
            <div className="space-y-3" data-testid="publish-intent-status">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${PUB_STATE[intent.status]?.cls}`} data-testid="publish-status-pill">{PUB_STATE[intent.status]?.label || intent.status}</span>
                {intent.youtube_video_id && <a href={`https://youtube.com/watch?v=${intent.youtube_video_id}`} target="_blank" rel="noreferrer" className="text-xs text-violet-300 underline">Open on YouTube</a>}
              </div>
              {["uploading"].includes(intent.status) && (
                <div className="w-full bg-slate-800 rounded-full h-2"><div className="bg-violet-500 h-2 rounded-full transition-all" style={{ width: `${intent.progress || 0}%` }} data-testid="publish-progress" /></div>
              )}
              <div className="text-xs text-slate-500 space-y-1">
                <div>Render v{intent.render_version} · privacy: {intent.privacy_status}{intent.publish_at ? ` · scheduled ${new Date(intent.publish_at).toLocaleString()}` : ""}</div>
                <div>Thumbnail: {intent.thumbnail_uploaded ? "uploaded" : "—"} · Captions: {intent.captions_uploaded ? "uploaded" : "—"} · Made for kids: {intent.made_for_kids ? "yes" : "no"} · Synthetic media: {intent.synthetic_media ? "disclosed" : "no"}</div>
              </div>
              {intent.status === "failed" && (
                <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="publish-error">
                  {intent.error_type === "quota" ? "YouTube API quota exceeded — try again later." : intent.error}
                </div>
              )}
              {active && <Button onClick={cancel} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="cancel-publish"><Ban className="w-4 h-4 mr-1.5" /> Cancel</Button>}
              {["failed", "cancelled"].includes(intent.status) && conn?.connected && (
                <Button onClick={publish} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="retry-publish"><RefreshCw className="w-4 h-4 mr-1.5" /> Retry</Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm mb-1.5 block">Privacy</Label>
                  <Select value={form.privacy_status} onValueChange={(v) => setForm({ ...form, privacy_status: v })}>
                    <SelectTrigger className={inputCls} data-testid="publish-privacy"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="unlisted">Unlisted</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={form.schedule} onCheckedChange={(v) => setForm({ ...form, schedule: v })} data-testid="publish-schedule-toggle" /> Schedule for later</label>
                </div>
                {form.schedule && (
                  <div className="sm:col-span-2"><Label className="text-slate-300 text-sm mb-1.5 block">Publish at (local time)</Label><Input type="datetime-local" className={inputCls} value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} data-testid="publish-at" /></div>
                )}
              </div>
              <div className="space-y-2 border-t border-slate-800 pt-3">
                <label className="flex items-center justify-between text-sm text-slate-300"><span>Made for kids</span><Switch checked={form.made_for_kids} onCheckedChange={(v) => setForm({ ...form, made_for_kids: v })} data-testid="publish-mfk" /></label>
                <label className="flex items-center justify-between text-sm text-slate-300"><span>Contains altered / AI-generated (synthetic) content</span><Switch checked={form.synthetic_media} onCheckedChange={(v) => setForm({ ...form, synthetic_media: v })} data-testid="publish-synthetic" /></label>
              </div>
              <Button onClick={publish} disabled={busy || !conn?.connected} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="publish-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1.5" /> {conn?.connected ? "Publish to YouTube" : "Connect YouTube to publish"}</>}
              </Button>
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-5">
        <Card>
          <h3 className="font-display font-semibold text-slate-100 mb-3 flex items-center gap-1.5"><Clock className="w-4 h-4" /> Internal schedule</h3>
          {data.schedule ? (
            <div className="text-sm text-slate-300" data-testid="publish-schedule-info">
              Scheduled for {new Date(data.schedule.scheduled_at).toLocaleString()} <span className="text-slate-500">({data.schedule.timezone})</span>
              <button onClick={() => navigate("/calendar")} className="block mt-2 text-xs text-violet-300 underline">Open calendar</button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Not on the calendar. You can schedule it from the <button onClick={() => navigate("/calendar")} className="text-violet-300 underline">Publishing Calendar</button> — this is independent of YouTube.</p>
          )}
        </Card>
        {intent?.checkpoints?.length > 0 && (
          <Card>
            <h3 className="font-display font-semibold text-slate-100 mb-3">Timeline</h3>
            <div className="space-y-1.5" data-testid="publish-checkpoints">
              {intent.checkpoints.map((c, i) => (
                <div key={`${c.name}-${c.at}`} className="flex items-center justify-between text-xs"><span className="text-slate-300">{c.name}</span><span className="text-slate-600">{new Date(c.at).toLocaleTimeString()}</span></div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
